import React, { useState, useMemo } from 'react';
import {
    TrendingUp,
    TrendingDown,
    Download,
    AlertTriangle,
    CheckCircle,
    XCircle,
    DollarSign,
    Clock,
    RefreshCw,
    Loader2
} from 'lucide-react';
import {
    AreaChart,
    Area,
    BarChart,
    Bar,
    PieChart as RechartsPie,
    Pie,
    Cell,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip,
    Legend,
    ResponsiveContainer,
    ComposedChart
} from 'recharts';
import {
    useLoans,
    useLoanStats,
    useComplianceHistory,
    useAlertsHistory,
    useAvgMaturity,
    useAtRiskLoans
} from '../hooks';
import { formatCurrency } from '../utils/helper';
import { downloadPortfolioReport } from '../utils/pdfExport';
import { exportPortfolioSummaryToExcel } from '../utils/excelExport';
import { useTranslation } from '../context/LanguageContext';


const COLORS = {
    primary: '#3b82f6',
    success: '#10b981',
    warning: '#f59e0b',
    danger: '#ef4444',
    purple: '#8b5cf6',
    cyan: '#06b6d4',
    pink: '#ec4899',
    slate: '#64748b',
};

const STATUS_COLORS = {
    ok: COLORS.success,
    warning: COLORS.warning,
    critical: COLORS.danger,
};


const generateComplianceHistory = (complianceRate = 0, hasData = false) => {

    if (!hasData || complianceRate === 0) {
        return [];
    }

    const months = ['Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    const baseCompliance = Math.max(complianceRate - 5, 75);
    return months.map((month, index) => ({
        month,
        compliance: Math.min(100, baseCompliance + index * 1.5),
        breaches: Math.max(0, 3 - Math.floor(index * 0.5)),
        warnings: Math.max(0, 5 - Math.floor(index * 0.7)),
    }));
};


const generateSectorData = (loans) => {
    const sectors = {};
    loans.forEach(loan => {
        if (!sectors[loan.sector]) {
            sectors[loan.sector] = { name: loan.sector, value: 0, amount: 0, count: 0 };
        }
        sectors[loan.sector].value += loan.amount;
        sectors[loan.sector].amount += loan.amount;
        sectors[loan.sector].count += 1;
    });
    return Object.values(sectors).sort((a, b) => b.value - a.value);
};


const generateStatusData = (loans, t) => {
    const stats = { ok: 0, warning: 0, critical: 0 };
    loans.forEach(loan => stats[loan.status]++);
    return [
        { name: t('dashboard.compliant'), value: stats.ok, color: COLORS.success },
        { name: t('dashboard.atRisk'), value: stats.warning, color: COLORS.warning },
        { name: t('dashboard.breach'), value: stats.critical, color: COLORS.danger },
    ];
};


const generateAlertsHistory = (criticalCount = 0, warningCount = 0, hasData = false) => {

    if (!hasData && criticalCount === 0 && warningCount === 0) {
        return [];
    }

    const months = ['Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    return months.map((month, index) => ({
        month,
        critical: Math.max(0, criticalCount - Math.floor(index * 0.3)),
        warning: Math.max(0, warningCount + Math.floor(index * 0.2)),
        info: 3 + Math.floor(index * 0.5),
    }));
};


const generateCountryData = (loans) => {
    const countries = {};
    loans.forEach(loan => {
        if (!countries[loan.country]) {
            countries[loan.country] = { name: loan.country, value: 0, count: 0 };
        }
        countries[loan.country].value += loan.amount;
        countries[loan.country].count += 1;
    });
    return Object.values(countries).sort((a, b) => b.value - a.value).slice(0, 6);
};


const KPICard = ({ title, value, subtitle, icon: Icon, trend, trendValue, color }) => (
    <div className="bg-white rounded-xl border border-slate-200 p-6">
        <div className="flex justify-between items-start">
            <div>
                <p className="text-sm text-slate-500 font-medium">{title}</p>
                <p className="text-3xl font-bold text-slate-800 mt-2">{value}</p>
                {subtitle && (
                    <div className="flex items-center gap-2 mt-2">
                        {trend && (
                            <span className={`flex items-center gap-1 text-sm font-medium ${
                                trend === 'up' ? 'text-emerald-600' : 'text-red-600'
                            }`}>
                {trend === 'up' ? <TrendingUp size={16} /> : <TrendingDown size={16} />}
                                {trendValue}
              </span>
                        )}
                        <span className="text-sm text-slate-500">{subtitle}</span>
                    </div>
                )}
            </div>
            <div className={`p-3 rounded-xl ${color}`}>
                <Icon size={24} className="text-white" />
            </div>
        </div>
    </div>
);


const ChartCard = ({ title, subtitle, children, action, className = '' }) => (
    <div className={`bg-white rounded-xl border border-slate-200 p-6 ${className}`}>
        <div className="flex justify-between items-start mb-6">
            <div>
                <h3 className="font-semibold text-slate-800">{title}</h3>
                {subtitle && <p className="text-sm text-slate-500 mt-1">{subtitle}</p>}
            </div>
            {action}
        </div>
        {children}
    </div>
);


const CustomTooltip = ({ active, payload, label }) => {
    if (!active || !payload) return null;
    return (
        <div className="bg-white border border-slate-200 rounded-lg shadow-lg p-3">
            <p className="font-medium text-slate-800 mb-2">{label}</p>
            {payload.map((entry, index) => (
                <p key={index} className="text-sm" style={{ color: entry.color }}>
                    {entry.name}: {typeof entry.value === 'number' && entry.value > 1000000
                    ? formatCurrency(entry.value)
                    : entry.value?.toFixed?.(1) || entry.value}
                </p>
            ))}
        </div>
    );
};


const dateRangeToMonths = (range) => {
    switch (range) {
        case '1m': return 1;
        case '3m': return 3;
        case '6m': return 6;
        case '1y': return 12;
        default: return 6;
    }
};

function Reports() {
    const { t } = useTranslation();
    const [dateRange, setDateRange] = useState('6m');
    const [isExporting, setIsExporting] = useState(false);


    const months = dateRangeToMonths(dateRange);


    const { loans: dbLoans, loading: loansLoading } = useLoans();
    const { stats: dbStats, loading: statsLoading } = useLoanStats();


    const { history: dbComplianceHistory, loading: complianceLoading } = useComplianceHistory(months);
    const { history: dbAlertsHistory, loading: alertsLoading } = useAlertsHistory(months);
    const { maturity: dbMaturity, loading: maturityLoading } = useAvgMaturity();
    const { loans: dbAtRiskLoans, loading: atRiskLoading } = useAtRiskLoans(5);


    const loans = useMemo(() => {
        return dbLoans.map(loan => ({
            id: loan.id,
            borrower: loan.borrower,
            amount: loan.amount,
            sector: loan.sector,
            country: loan.country,
            status: loan.status,
            covenants: loan.covenants || [],
            issuesCount: loan.issues_count || 0
        }));
    }, [dbLoans]);


    const stats = useMemo(() => {
        if (!dbStats) {
            return {
                totalLoans: 0,
                totalValue: 0,
                complianceRate: 0,
                critical: 0,
                warning: 0,
                ok: 0,
                avgMaturity: 'N/A'
            };
        }
        return {
            totalLoans: dbStats.totalLoans || 0,
            totalValue: dbStats.totalAmount || 0,
            complianceRate: dbStats.complianceRate || 0,
            critical: dbStats.breachedCovenants || 0,
            warning: dbStats.atRiskCovenants || 0,
            ok: dbStats.compliantCovenants || 0,
            avgMaturity: dbMaturity?.display || 'N/A'
        };
    }, [dbStats, dbMaturity]);


    const complianceHistory = useMemo(() => {
        if (dbComplianceHistory && dbComplianceHistory.length > 0) {
            return dbComplianceHistory;
        }
        return generateComplianceHistory(stats.complianceRate);
    }, [dbComplianceHistory, stats.complianceRate]);

    const alertsHistory = useMemo(() => {
        if (dbAlertsHistory && dbAlertsHistory.length > 0) {
            return dbAlertsHistory;
        }
        return generateAlertsHistory(stats.critical, stats.warning);
    }, [dbAlertsHistory, stats.critical, stats.warning]);

    const sectorData = useMemo(() => generateSectorData(loans), [loans]);
    const statusData = useMemo(() => generateStatusData(loans, t), [loans, t]);
    const countryData = useMemo(() => generateCountryData(loans), [loans]);


    const atRiskLoans = useMemo(() => {
        if (dbAtRiskLoans && dbAtRiskLoans.length > 0) {
            return dbAtRiskLoans.map(loan => ({
                id: loan.id,
                borrower: loan.borrower,
                amount: loan.amount,
                status: loan.status,
                issuesCount: loan.issues_count || 0
            }));
        }
        return loans
            .filter(l => l.status !== 'ok')
            .sort((a, b) => (b.status === 'critical' ? 1 : 0) - (a.status === 'critical' ? 1 : 0))
            .slice(0, 5);
    }, [dbAtRiskLoans, loans]);


    const sectorHealth = useMemo(() => {
        const health = {};
        loans.forEach(loan => {
            if (!health[loan.sector]) {
                health[loan.sector] = { sector: loan.sector, compliant: 0, atRisk: 0, breach: 0 };
            }
            if (loan.status === 'ok') health[loan.sector].compliant++;
            else if (loan.status === 'warning') health[loan.sector].atRisk++;
            else health[loan.sector].breach++;
        });
        return Object.values(health);
    }, [loans]);


    const trends = useMemo(() => {

        let complianceTrend = null;
        if (complianceHistory.length >= 2) {
            const currentCompliance = complianceHistory[complianceHistory.length - 1]?.compliance || stats.complianceRate;
            const previousCompliance = complianceHistory[0]?.compliance || stats.complianceRate;
            const diff = currentCompliance - previousCompliance;
            complianceTrend = {
                direction: diff >= 0 ? 'up' : 'down',
                value: `${diff >= 0 ? '+' : ''}${diff.toFixed(1)}%`
            };
        }


        let alertsTrend = null;
        if (alertsHistory.length >= 2) {
            const currentAlerts = alertsHistory[alertsHistory.length - 1];
            const previousAlerts = alertsHistory[alertsHistory.length - 2];
            const currentTotal = (currentAlerts?.critical || 0) + (currentAlerts?.warning || 0);
            const previousTotal = (previousAlerts?.critical || 0) + (previousAlerts?.warning || 0);
            const diff = currentTotal - previousTotal;

            alertsTrend = {
                direction: diff <= 0 ? 'up' : 'down',
                value: `${diff <= 0 ? '' : '+'}${diff}`
            };
        }

        return { complianceTrend, alertsTrend };
    }, [complianceHistory, alertsHistory, stats.complianceRate]);


    const handleExport = async (format) => {
        setIsExporting(true);
        try {
            if (format === 'pdf') {
                downloadPortfolioReport(loans, stats);
            }

        } finally {
            setTimeout(() => setIsExporting(false), 1000);
        }
    };


    if (loansLoading || statsLoading) {
        return (
            <div className="flex items-center justify-center min-h-[400px]">
                <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
            </div>
        );
    }

    return (
        <div className="space-y-6">
            {}
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
                <div>
                    <h2 className="text-xl sm:text-2xl font-bold text-slate-800">{t('nav.reports')}</h2>
                    <p className="text-slate-500 text-sm sm:text-base">{t('reports.subtitle')}</p>
                </div>
                <div className="flex gap-2 sm:gap-3 w-full sm:w-auto">
                    <select
                        value={dateRange}
                        onChange={(e) => setDateRange(e.target.value)}
                        className="flex-1 sm:flex-none px-3 sm:px-4 py-2 border border-slate-300 rounded-lg text-slate-700 text-sm"
                    >
                        <option value="1m">{t('dateTime.lastMonth')}</option>
                        <option value="3m">{t('dateTime.last3Months')}</option>
                        <option value="6m">{t('dateTime.last6Months')}</option>
                        <option value="1y">{t('dateTime.lastYear')}</option>
                    </select>
                    <button
                        onClick={() => handleExport('pdf')}
                        disabled={isExporting || !loans || loans.length === 0}
                        className={`flex items-center gap-2 px-3 sm:px-4 py-2 rounded-lg text-sm whitespace-nowrap ${
                            loans && loans.length > 0
                                ? 'bg-blue-600 text-white hover:bg-blue-700'
                                : 'bg-slate-300 text-slate-500 cursor-not-allowed'
                        } disabled:opacity-50`}
                    >
                        {isExporting ? (
                            <RefreshCw size={18} className="animate-spin" />
                        ) : (
                            <Download size={18} />
                        )}
                        <span className="hidden sm:inline">{t('loans.exportReport')}</span>
                        <span className="sm:hidden">{t('common.export')}</span>
                    </button>
                </div>
            </div>

            {}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                <KPICard
                    title={t('dashboard.totalAmount')}
                    value={formatCurrency(stats.totalValue)}
                    subtitle={`${stats.totalLoans} ${t('nav.loans').toLowerCase()}`}
                    icon={DollarSign}
                    color="bg-blue-500"
                />
                <KPICard
                    title={t('dashboard.complianceRate')}
                    value={`${stats.complianceRate}%`}
                    subtitle={trends.complianceTrend ? t('reports.overPeriod') : undefined}
                    trend={trends.complianceTrend?.direction}
                    trendValue={trends.complianceTrend?.value}
                    icon={CheckCircle}
                    color="bg-emerald-500"
                />
                <KPICard
                    title={t('alerts.critical')}
                    value={stats.critical}
                    subtitle={trends.alertsTrend ? t('reports.requiresAction') : undefined}
                    trend={trends.alertsTrend?.direction}
                    trendValue={trends.alertsTrend?.value}
                    icon={XCircle}
                    color="bg-red-500"
                />
                <KPICard
                    title={t('reports.avgMaturity')}
                    value={stats.avgMaturity}
                    subtitle={t('reports.portfolioAvg')}
                    icon={Clock}
                    color="bg-purple-500"
                />
            </div>

            {}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                {}
                <ChartCard
                    title={t('reports.complianceTrend')}
                    subtitle={t('reports.historicalView')}
                    className="lg:col-span-2"
                >
                    <div className="h-72">
                        <ResponsiveContainer width="100%" height="100%">
                            <ComposedChart data={complianceHistory}>
                                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                                <XAxis dataKey="month" stroke="#64748b" fontSize={12} />
                                <YAxis yAxisId="left" stroke="#64748b" fontSize={12} domain={[80, 100]} />
                                <YAxis yAxisId="right" orientation="right" stroke="#64748b" fontSize={12} />
                                <Tooltip content={<CustomTooltip />} />
                                <Legend />
                                <Area
                                    yAxisId="left"
                                    type="monotone"
                                    dataKey="compliance"
                                    name="Compliance %"
                                    fill={COLORS.primary}
                                    fillOpacity={0.2}
                                    stroke={COLORS.primary}
                                    strokeWidth={2}
                                />
                                <Bar yAxisId="right" dataKey="breaches" name="Breaches" fill={COLORS.danger} radius={[4, 4, 0, 0]} />
                                <Bar yAxisId="right" dataKey="warnings" name="Warnings" fill={COLORS.warning} radius={[4, 4, 0, 0]} />
                            </ComposedChart>
                        </ResponsiveContainer>
                    </div>
                </ChartCard>

                {}
                <ChartCard title={t('reports.portfolioStatus')} subtitle={t('reports.currentDistribution')}>
                    <div className="h-72 flex items-center justify-center">
                        <ResponsiveContainer width="100%" height="100%">
                            <RechartsPie>
                                <Pie
                                    data={statusData}
                                    cx="50%"
                                    cy="50%"
                                    innerRadius={60}
                                    outerRadius={90}
                                    paddingAngle={5}
                                    dataKey="value"
                                    label={({ name, value }) => `${name}: ${value}`}
                                    labelLine={false}
                                >
                                    {statusData.map((entry, index) => (
                                        <Cell key={index} fill={entry.color} />
                                    ))}
                                </Pie>
                                <Tooltip />
                            </RechartsPie>
                        </ResponsiveContainer>
                    </div>
                    <div className="flex justify-center gap-6 mt-4">
                        {statusData.map((item, index) => (
                            <div key={index} className="flex items-center gap-2">
                                <div className="w-3 h-3 rounded-full" style={{ backgroundColor: item.color }} />
                                <span className="text-sm text-slate-600">{item.name}</span>
                            </div>
                        ))}
                    </div>
                </ChartCard>
            </div>

            {}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {}
                <ChartCard title={t('reports.exposureBySector')} subtitle={t('reports.portfolioAllocation')}>
                    <div className="h-72">
                        <ResponsiveContainer width="100%" height="100%">
                            <BarChart data={sectorData} layout="vertical">
                                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                                <XAxis type="number" stroke="#64748b" fontSize={12} tickFormatter={(v) => formatCurrency(v)} />
                                <YAxis type="category" dataKey="name" stroke="#64748b" fontSize={11} width={100} />
                                <Tooltip content={<CustomTooltip />} />
                                <Bar dataKey="value" name="Exposure" fill={COLORS.primary} radius={[0, 4, 4, 0]}>
                                    {sectorData.map((entry, index) => (
                                        <Cell key={index} fill={[COLORS.primary, COLORS.cyan, COLORS.purple, COLORS.pink, COLORS.success, COLORS.warning][index % 6]} />
                                    ))}
                                </Bar>
                            </BarChart>
                        </ResponsiveContainer>
                    </div>
                </ChartCard>

                {}
                <ChartCard title={t('reports.alertsTrend')} subtitle={t('reports.monthlyAlertVolume')}>
                    <div className="h-72">
                        <ResponsiveContainer width="100%" height="100%">
                            <AreaChart data={alertsHistory}>
                                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                                <XAxis dataKey="month" stroke="#64748b" fontSize={12} />
                                <YAxis stroke="#64748b" fontSize={12} />
                                <Tooltip content={<CustomTooltip />} />
                                <Legend />
                                <Area type="monotone" dataKey="critical" name="Critical" stackId="1" fill={COLORS.danger} stroke={COLORS.danger} />
                                <Area type="monotone" dataKey="warning" name="Warning" stackId="1" fill={COLORS.warning} stroke={COLORS.warning} />
                                <Area type="monotone" dataKey="info" name="Info" stackId="1" fill={COLORS.primary} stroke={COLORS.primary} />
                            </AreaChart>
                        </ResponsiveContainer>
                    </div>
                </ChartCard>
            </div>

            {}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {}
                <ChartCard title={t('reports.geographicExposure')} subtitle={t('reports.topCountries')}>
                    <div className="h-64">
                        <ResponsiveContainer width="100%" height="100%">
                            <BarChart data={countryData}>
                                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                                <XAxis dataKey="name" stroke="#64748b" fontSize={11} />
                                <YAxis stroke="#64748b" fontSize={12} tickFormatter={(v) => `€${v/1e6}M`} />
                                <Tooltip content={<CustomTooltip />} />
                                <Bar dataKey="value" name="Exposure" fill={COLORS.cyan} radius={[4, 4, 0, 0]} />
                            </BarChart>
                        </ResponsiveContainer>
                    </div>
                </ChartCard>

                {}
                <ChartCard title={t('reports.covenantHealthBySector')} subtitle={t('reports.statusBreakdown')}>
                    <div className="h-64">
                        <ResponsiveContainer width="100%" height="100%">
                            <BarChart data={sectorHealth} layout="vertical">
                                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                                <XAxis type="number" stroke="#64748b" fontSize={12} />
                                <YAxis type="category" dataKey="sector" stroke="#64748b" fontSize={10} width={80} />
                                <Tooltip />
                                <Legend />
                                <Bar dataKey="compliant" name="Compliant" stackId="a" fill={COLORS.success} />
                                <Bar dataKey="atRisk" name="At Risk" stackId="a" fill={COLORS.warning} />
                                <Bar dataKey="breach" name="Breach" stackId="a" fill={COLORS.danger} />
                            </BarChart>
                        </ResponsiveContainer>
                    </div>
                </ChartCard>

                {}
                <ChartCard title={t('reports.topAtRiskLoans')} subtitle={t('reports.requiresAttention')}>
                    <div className="space-y-3">
                        {atRiskLoans.map((loan, index) => (
                            <div key={loan.id} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                                <div className="flex items-center gap-3">
                                    <div className={`w-8 h-8 rounded-full flex items-center justify-center text-white text-sm font-medium ${
                                        loan.status === 'critical' ? 'bg-red-500' : 'bg-amber-500'
                                    }`}>
                                        {index + 1}
                                    </div>
                                    <div>
                                        <p className="font-medium text-slate-800 text-sm">{loan.borrower}</p>
                                        <p className="text-xs text-slate-500">{formatCurrency(loan.amount)}</p>
                                    </div>
                                </div>
                                <div className="flex items-center gap-2">
                                    {loan.status === 'critical' ? (
                                        <XCircle size={18} className="text-red-500" />
                                    ) : (
                                        <AlertTriangle size={18} className="text-amber-500" />
                                    )}
                                    <span className="text-xs text-slate-500">
                                        {loan.issuesCount || loan.issues_count || 0} issues
                                    </span>
                                </div>
                            </div>
                        ))}
                    </div>
                </ChartCard>
            </div>

            {}
            <ChartCard
                title={t('reports.portfolioSummary')}
                subtitle={t('reports.completeOverview')}
                action={
                    <button
                        onClick={() => exportPortfolioSummaryToExcel(sectorData, sectorHealth, stats, t)}
                        disabled={!sectorData || sectorData.length === 0}
                        className={`flex items-center gap-2 text-sm ${
                            sectorData && sectorData.length > 0
                                ? 'text-blue-600 hover:underline'
                                : 'text-slate-400 cursor-not-allowed'
                        }`}
                    >
                        <Download size={16} />
                        {t('reports.exportToExcel')}
                    </button>
                }
            >
                <div className="overflow-x-auto">
                    <table className="w-full">
                        <thead className="bg-slate-50">
                        <tr>
                            <th className="text-left px-4 py-3 text-sm font-semibold text-slate-600">{t('loans.sector')}</th>
                            <th className="text-right px-4 py-3 text-sm font-semibold text-slate-600">{t('nav.loans')}</th>
                            <th className="text-right px-4 py-3 text-sm font-semibold text-slate-600">{t('reports.exposure')}</th>
                            <th className="text-right px-4 py-3 text-sm font-semibold text-slate-600">{t('reports.percentPortfolio')}</th>
                            <th className="text-right px-4 py-3 text-sm font-semibold text-slate-600">{t('dashboard.compliant')}</th>
                            <th className="text-right px-4 py-3 text-sm font-semibold text-slate-600">{t('dashboard.atRisk')}</th>
                            <th className="text-right px-4 py-3 text-sm font-semibold text-slate-600">{t('dashboard.breach')}</th>
                            <th className="text-right px-4 py-3 text-sm font-semibold text-slate-600">{t('reports.compliance')} %</th>
                        </tr>
                        </thead>
                        <tbody className="divide-y divide-slate-100">
                        {sectorData.map((sector, index) => {
                            const health = sectorHealth.find(s => s.sector === sector.name) || { compliant: 0, atRisk: 0, breach: 0 };
                            const total = health.compliant + health.atRisk + health.breach;
                            const complianceRate = total > 0 ? ((health.compliant / total) * 100).toFixed(0) : 0;
                            const portfolioShare = stats.totalValue > 0 ? ((sector.value / stats.totalValue) * 100).toFixed(1) : '0.0';

                            return (
                                <tr key={index} className="hover:bg-slate-50">
                                    <td className="px-4 py-3 font-medium text-slate-800">{sector.name}</td>
                                    <td className="px-4 py-3 text-right text-slate-600">{sector.count}</td>
                                    <td className="px-4 py-3 text-right font-medium text-slate-800">{formatCurrency(sector.value)}</td>
                                    <td className="px-4 py-3 text-right text-slate-600">{portfolioShare}%</td>
                                    <td className="px-4 py-3 text-right text-emerald-600 font-medium">{health.compliant}</td>
                                    <td className="px-4 py-3 text-right text-amber-600 font-medium">{health.atRisk}</td>
                                    <td className="px-4 py-3 text-right text-red-600 font-medium">{health.breach}</td>
                                    <td className="px-4 py-3 text-right">
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                          complianceRate >= 80
                              ? 'bg-emerald-100 text-emerald-700'
                              : complianceRate >= 60
                                  ? 'bg-amber-100 text-amber-700'
                                  : 'bg-red-100 text-red-700'
                      }`}>
                        {complianceRate}%
                      </span>
                                    </td>
                                </tr>
                            );
                        })}
                        </tbody>
                        <tfoot className="bg-slate-50 font-medium">
                        <tr>
                            <td className="px-4 py-3 text-slate-800">Total</td>
                            <td className="px-4 py-3 text-right text-slate-800">{stats.totalLoans}</td>
                            <td className="px-4 py-3 text-right text-slate-800">{formatCurrency(stats.totalValue)}</td>
                            <td className="px-4 py-3 text-right text-slate-800">{stats.totalLoans > 0 ? '100%' : '0%'}</td>
                            <td className="px-4 py-3 text-right text-emerald-600">{stats.ok}</td>
                            <td className="px-4 py-3 text-right text-amber-600">{stats.warning}</td>
                            <td className="px-4 py-3 text-right text-red-600">{stats.critical}</td>
                            <td className="px-4 py-3 text-right">
                  <span className="px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-700">
                    {stats.complianceRate}%
                  </span>
                            </td>
                        </tr>
                        </tfoot>
                    </table>
                </div>
            </ChartCard>
        </div>
    );
}

export default Reports;